/* eslint-disable react-refresh/only-export-components -- factory function, not a component file */
import { lazy, memo, Suspense } from 'react'
import type { ComponentType } from 'react'
import type { PanelProps } from '@/workspace/types'

/**
 * Custom comparison for memo'd panels. Only re-renders when data props change.
 * Callback props (onSelectTicker, onFocusDate, onRegister*) are excluded because
 * they are inline closures that change identity on every parent render but don't
 * affect the panel's visual output — panels call them in response to user actions,
 * not during rendering.
 */
function panelPropsAreEqual(prev: PanelProps, next: PanelProps): boolean {
  return (
    prev.panelId === next.panelId &&
    prev.windowConfig === next.windowConfig &&
    prev.symbols === next.symbols &&
    prev.focusDate === next.focusDate &&
    prev.focusDateSource === next.focusDateSource
  )
}

/**
 * Wraps a dynamic import with a single retry on failure.
 * Chunk load errors are common after deploys when the old chunk hashes are gone.
 */
function lazyRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch(() => {
      // One retry after a short delay — covers transient network blips
      return new Promise<{ default: T }>((resolve) => setTimeout(() => resolve(importFn()), 1500))
    }),
  )
}

function PanelSkeleton() {
  return (
    // No background — the mosaic window paints the panel's gradient surface,
    // so first paint shows the empty gradient panel with a spinner on it.
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
    </div>
  )
}

/**
 * Creates a lazy-loaded panel component with Suspense and ErrorBoundary built in.
 *
 * Usage in component-registry:
 *   chart: lazyPanel(() => import('@/features/charts/chart-panel'), 'ChartPanel'),
 */
export const lazyPanel = (
  importFn: () => Promise<Record<string, any>>,
  exportName: string,
): ComponentType<PanelProps> => {
  const LazyComp = lazyRetry(() =>
    importFn().then((mod) => ({ default: mod[exportName] as ComponentType<PanelProps> })),
  )

  const MemoLazyComp = memo(LazyComp, panelPropsAreEqual)

  function LazyPanelWrapper(props: PanelProps) {
    return (
      <Suspense fallback={<PanelSkeleton />}>
        <MemoLazyComp {...props} />
      </Suspense>
    )
  }

  LazyPanelWrapper.displayName = `LazyPanel(${exportName})`
  return LazyPanelWrapper
}
